// Obavijesti na mobitel: service worker (public/sw.js) i pretplata ovog uređaja. Javni ključ daje server (svaki server
// ima svoj par), pretplatu sprema baza (push_claim2), a obavijesti šalje Edge funkcija push za svaku novu obavijest.

export type PushState = 'unsupported' | 'ios-install' | 'denied' | 'off' | 'on';

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const standalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
const ios = () => /iPhone|iPad|iPod/.test(navigator.userAgent);

/** Registracija service workera (jednom, pri pokretanju aplikacije). */
export function registerSW() {
  if ('serviceWorker' in navigator) void navigator.serviceWorker.register('/sw.js').catch(() => {});
}

const b64u = (s: string) => {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
};
const post = async (path: string, body: object = {}) => {
  const res = await fetch(path, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json();
};

// javni ključ ovog servera (jednom po otvaranju)
let keyP: Promise<Uint8Array<ArrayBuffer> | null> | null = null;
const serverKey = () =>
  (keyP ??= fetch('/v1/push/config')
    .then(r => r.json() as Promise<{ vapidPublic: string }>)
    .then(j => (j.vapidPublic ? b64u(j.vapidPublic) : null))
    .catch(() => {
      keyP = null;
      return null;
    }));
const sameKey = (sub: PushSubscription, key: Uint8Array<ArrayBuffer>) => {
  const old = sub.options.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null;
  return !!old && old.length === key.length && old.every((b, i) => b === key[i]);
};

export async function pushState(): Promise<PushState> {
  // iPhone: obavijesti rade samo kad je Lobi dodan na početni zaslon
  if (ios() && !standalone()) return 'ios-install';
  if (!supported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub || Notification.permission !== 'granted') return 'off';
  // pretplata sa starog servera (drugi ključ) ne prima ništa: kao da je isključeno (prelazak 21. 10. 2026.)
  const key = await serverKey();
  return key && !sameKey(sub, key) ? 'off' : 'on';
}

// Igrač je sam isključio obavijesti u Postavkama: aplikacija ih tada ne pali sama (dozvola preglednika ostaje „dopušteno”).
const OFF_KEY = 'lobi-push-off';
const userOff = () => {
  try {
    return localStorage.getItem(OFF_KEY) === '1';
  } catch {
    return false;
  }
};
const setUserOff = (v: boolean) => {
  try {
    if (v) localStorage.setItem(OFF_KEY, '1');
    else localStorage.removeItem(OFF_KEY);
  } catch {
    /* privatni prozor */
  }
};

/** Pretplata s ključem ovog servera (bez pitanja, dozvola je već dana) i spremanje; odbijenu (Apple/Google) zamijeni novom. */
async function subscribe(key: Uint8Array<ArrayBuffer>) {
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  // stara pretplata s drugim ključem (npr. drugi server) ne vrijedi
  if (sub && !sameKey(sub, key)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  if ((await save(sub)).status === 'gone') {
    await sub.unsubscribe();
    await save(await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }));
  }
}

/**
 * Pri otvaranju aplikacije (prijavljen): pretplatu ovog uređaja tiho ponovno spremi na server, a kad je preglednik izgubio
 * ili zamijenio pretplatu (Apple i Google to rade sami, npr. nakon ažuriranja), dok je dozvola i dalje dana, napravi novu
 * bez ikakvog pitanja (tiket #21: obavijesti su se „same gasile” pa ih je trebalo ponovno paliti). Ne kad ih je igrač
 * sam isključio.
 */
export async function resyncPush() {
  if ((ios() && !standalone()) || !supported() || Notification.permission !== 'granted' || userOff()) return;
  const key = await serverKey();
  if (key) await subscribe(key).catch(() => {});
}

async function save(sub: PushSubscription) {
  const j = sub.toJSON();
  return (await post('/v1/push/subscribe', { endpoint: j.endpoint, p256dh: j.keys?.p256dh, auth: j.keys?.auth })) as {
    status: 'ok' | 'gone';
  };
}

/** Uključi: dozvola, pretplata s ključem servera, spremanje; pretplatu koju je Apple/Google odbio zamijeni novom. */
export async function enablePush(): Promise<PushState> {
  const key = await serverKey();
  if (!key) return 'unsupported';
  if ((await Notification.requestPermission()) !== 'granted') return Notification.permission === 'denied' ? 'denied' : 'off';
  await subscribe(key);
  setUserOff(false);
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  setUserOff(true);
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await post('/v1/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe();
  }
  return 'off';
}

/**
 * Odjava: odjavljeni igrač na ovom uređaju više ne dobiva obavijesti. Pretplata se makne samo na serveru (dok je sesija
 * još valjana); dozvola i pretplata preglednika ostaju, pa tko se sljedeći prijavi ovdje dobiva obavijesti bez pitanja
 * (resyncPush). lobi-push-off se ne upisuje: igrač nije sam isključio obavijesti.
 */
export async function dropPushOnSignOut() {
  if (!supported()) return;
  const sub = await (await navigator.serviceWorker.getRegistration())?.pushManager.getSubscription();
  if (sub) await post('/v1/push/unsubscribe', { endpoint: sub.endpoint });
}

export const testPush = () => post('/v1/push/test');

/** Id ovog uređaja za Admin → uređaji igrača: nasumičan, živi samo u ovom pregledniku. */
const deviceId = () => {
  try {
    let id = localStorage.getItem('lobi-device');
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem('lobi-device', id);
    }
    return id;
  } catch {
    return null; // privatni prozor: uređaj se ne bilježi
  }
};

/** Jednom po otvaranju (prijavljen): stanje ovog uređaja (instaliran kao aplikacija, obavijesti); sustav i preglednik određuje server. */
let reported = '';
export async function reportDevice(force = false) {
  const device = deviceId();
  // jednom po otvaranju (React u razvoju i ponovna iscrtavanja zovu više puta), osim nakon promjene obavijesti
  if (!device || (reported === device && !force)) return;
  reported = device;
  const push = await pushState().catch(() => 'unsupported' as const);
  await post('/v1/device', { device, standalone: standalone(), push }).catch(() => {});
}
